import React from 'react';
import { CotasDivergentes } from '../utils/cotasDivergentes';
import { TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';

const brl = (v: number) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * O que mostrar quando o backend recusa o rateio com 409 `cotas_acordadas_divergem`:
 * a mensagem dele, uma linha por empresa (acordado, calculado, diferença com sinal)
 * e a escolha EXPLÍCITA de recalcular, que nunca vem marcada.
 *
 * Quem usa: a tela de editar/aplicar o rateio (Fase 1 do front). Enquanto
 * `recalcular` estiver falso, o Salvar dessa tela fica desabilitado; marcado, o
 * reenvio leva `recalcularCotas: true` (`rateioService.salvar`).
 */
interface Props {
  divergencia: CotasDivergentes;
  recalcular: boolean;
  onRecalcular: (v: boolean) => void;
}

const AvisoDeCotasDivergentes: React.FC<Props> = ({ divergencia, recalcular, onRecalcular }) => (
  <div className={`rounded-lg border p-4 space-y-3 ${TONE_SURFACE.warning} ${TONE_BORDER.warning}`} role='alert'>
    <p className={`text-sm font-medium ${TONE_TEXT.warning}`}>{divergencia.mensagem}</p>
    <table className='w-full text-sm'>
      <thead>
        <tr className='text-[10px] uppercase tracking-wider text-slate-500'>
          <th className='text-left font-medium py-1'>Empresa</th>
          <th className='text-right font-medium py-1'>Acordado</th>
          <th className='text-right font-medium py-1'>Calculado</th>
          <th className='text-right font-medium py-1'>Diferença</th>
        </tr>
      </thead>
      <tbody>
        {divergencia.empresas.map((e) => (
          <tr key={e.clientId} className='border-t border-slate-700/60'>
            <td className='py-1.5 text-slate-200 truncate'>{e.nome}</td>
            <td className='py-1.5 text-right tabular-nums text-slate-300'>R$ {brl(e.acordado)}</td>
            <td className='py-1.5 text-right tabular-nums text-slate-300'>R$ {brl(e.calculado)}</td>
            {/* §11.2: vermelho quando negativo, verde quando positivo. */}
            <td
              className={`py-1.5 text-right tabular-nums font-medium ${e.deltaReais < 0 ? TONE_TEXT.danger : e.deltaReais > 0 ? TONE_TEXT.open : 'text-slate-300'}`}
              data-testid={`delta-${e.clientId}`}
            >
              {e.deltaReais > 0 ? '+' : e.deltaReais < 0 ? '−' : ''}R$ {brl(Math.abs(e.deltaReais))}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    <label className='flex items-start gap-2 cursor-pointer'>
      <input
        type='checkbox'
        checked={recalcular}
        onChange={(ev) => onRecalcular(ev.target.checked)}
        className='mt-0.5 accent-orange-500'
      />
      <span className='text-sm text-slate-300'>
        Recalcular as cotas acordadas
        <span className='block text-xs text-slate-500'>
          Muda o valor da cota dessas empresas a partir da próxima fatura ({divergencia.competenciaInicio}).
        </span>
      </span>
    </label>
  </div>
);

export default AvisoDeCotasDivergentes;
